import { buildReceiptPdfSummary } from './receiptPdfSummary';

import { getContactDocumentName } from '../../utils/contactDisplayName';
import { buildReceiptItemDescription, buildReceiptItemQuantity } from './receiptItemDisplay';

export const createDocDefinitionFullStyle = ({
    printType,
    documentType,
    documentNumber,
    company,
    receipt,
    logoBase64,
    fixedSignatureLogo,
    fixedStampLogo,
    signatureLayout = { x: 0, y: 0, width: 100 },
    printOptions = {},
    totalAmount,
    paidAmount,
    remainingAmount,
    numberToThaiText,
    formatCurrency,
    dayjs
}) => {
    const signaturePosition = {
        x: Number(signatureLayout.x) || 0,
        y: Number(signatureLayout.y) || 0,
        width: Math.min(Math.max(Number(signatureLayout.width) || 100, 40), 180)
    };
    const summary = buildReceiptPdfSummary(receipt);
    const amountAfterDiscountLabel = summary.vatMode === 'no_vat'
        ? 'ยอดหลังหักส่วนลด:'
        : 'ราคาไม่รวมภาษี:';

    const showTaxBreakdown = summary.vatMode !== 'no_vat' || summary.taxAmount > 0;
    const taxLabel = summary.taxRate > 0 ? `ภาษี ${summary.taxRate}%:` : 'ภาษี:';
    const pdfTotalAmount = summary.grandTotal || totalAmount;
    const customerDisplayName = getContactDocumentName(receipt);
    const salesPerson = printOptions.salesperson_name
        || receipt.salesperson_name
        || receipt.sale_name
        || receipt.created_by_name
        || '-';
    const collectorName = printOptions.collector_name
        || company.signature_name
        || receipt.created_by_name
        || 'ผู้รับเงิน';

    return {
    pageSize: 'A4',
    pageMargins: [23, 30, 23, 30], //left, top, right, bottom
    defaultStyle: {
        font: 'THSarabunNew',
        fontSize: 16,
        lineHeight: 0.9   // <— ค่านี้ยิ่งน้อย บรรทัดจะยิ่งชิดกัน
    },
    styles: {
        header: {
            fontSize: 25,
            bold: true,
            alignment: 'center',
            margin: [0, 0, 0, 20]
        },
        subheader: {
            fontSize: 16,
            bold: true,
            margin: [0, 10, 0, 5]
        },
        tableHeader: {
            bold: true,
            fontSize: 15,
            color: 'black',
            fillColor: '#eeeeee'
        },
        total: {
            fontSize: 16,
            bold: true,
            alignment: 'right'
        },
        paid: {
            fontSize: 16,
            bold: true,
            color: '#52c41a'
        },
        overdue: {
            fontSize: 16,
            bold: true,
            color: '#cf1322'
        }
    },
    content: [
        // หัวข้อเอกสาร
        {
            text: printType === 'full' ? documentType : '',
            style: 'header',
            alignment: 'right'
        },

        // =============================
        // ส่วนข้อมูลบริษัท (มีพื้นหลังฟ้าอ่อน)
        // =============================
        {
            table: {
                widths: ['60%', '40%'],
                body: [
                    [
                        {
                            columns: [
                                {
                                    ...(logoBase64
                                        ? { image: logoBase64, width: 80, alignment: 'left', margin: [0, 18, 0, 0] }
                                        : { text: '', fontSize: 18, bold: true, margin: [0, 18, 0, 0] }
                                    ),
                                },
                                {
                                    stack: [
                                        { text: company.company_name || 'ชื่อบริษัท', bold: true, fontSize: 16 },
                                        { text: company.address || 'ที่อยู่บริษัท', margin: [0, 0, 0, 0] },
                                        { text: `โทรศัพท์: ${company.phone || 'หมายเลขโทรศัพท์'}`, margin: [0, 0, 0, 0] },
                                        { text: `เลขที่ภาษี: ${company.tax_id || '-'}`, margin: [0, 0, 0, 0] }
                                    ],
                                    margin: [10, 10, 0, 10]
                                },
                            ],
                            border: [false, false, false, false],
                            margin: [0, -65, 0, 10]
                        },
                        {
                            stack: [
                                {
                                    canvas: [
                                        {
                                            type: 'rect',
                                            x: 0,
                                            y: 0,
                                            w: 160, // กำหนดความกว้างของพื้นหลัง (ปรับได้)
                                            h: 65,  // รองรับเลขที่ วันที่ และชื่อพนักงานขาย
                                            r: 10,  // ความมนของขอบ 10px
                                            color: '#E3F2FD' // 💙 สีฟ้าอ่อน
                                        }
                                    ],
                                    absolutePosition: { x: 350, y: 97 }, // ปรับตำแหน่งตาม layout ของคุณ
                                },
                                {
                                    text: [
                                        { text: printType === 'full' ? 'เลขที่เอกสาร: ' : '', bold: true },
                                        { text: documentNumber || '-', bold: false },

                                    ],
                                    margin: [0, 0, 20, 0],
                                },
                                {
                                    text: [
                                        { text: printType === 'full' ? 'วันที่: ' : '', bold: true },
                                        { text: dayjs(receipt.receipt_date).format('DD/MM/YYYY'), bold: false }
                                    ],
                                    margin: [0, 0, 20, 0],
                                },
                                {
                                    text: [
                                        { text: printType === 'full' ? 'พนักงานขาย: ' : '', bold: true },
                                        { text: salesPerson, bold: false }
                                    ],
                                    margin: [0, 0, 20, 0],
                                },
                                // ถ้าต้องการเพิ่มวันหมดอายุ uncomment ตรงนี้
                                // {
                                //   text: [
                                //     { text: 'วันหมดอายุ: ', bold: true },
                                //     { text: dayjs(quote.validUntil).format('DD/MM/YYYY'), bold: false }
                                //   ],
                                //   margin: [0, 2, 0, 0]
                                // }
                            ],
                            alignment: 'right',
                            border: [false, false, false, false],
                            margin: [0, 18, 0, 0]
                        }
                    ]
                ]
            },
            margin: [0, 0, 0, 20]
        },

        // ข้อมูลลูกค้า
        // {
        //     text: 'ข้อมูลลูกค้า',
        //     style: 'subheader'
        // },
        {
            lineHeight: 0.7,
            fontSize: 15,
            text: [
                { text: 'ชื่อลูกค้า: ', bold: true },
                customerDisplayName,
                '\n',
                { text: 'ที่อยู่: ', bold: true },
                receipt.address || '-',
                '\n',
                { text: 'โทรศัพท์: ', bold: true },
                receipt.phone || '-',
                // '\n',
                // { text: 'อีเมล: ', bold: true },
                // receipt.email || '-',
                '\n',
                { text: 'เลขที่ภาษี: ', bold: true },
                receipt.tax_id || '-'

            ],
            margin: [10, -25, 0, 50]
        },

        // รายการสินค้า/บริการ
        // {
        //     text: 'รายการสินค้า/บริการ',
        //     style: 'subheader'
        // },
        {
            table: {
                headerRows: 0,
                widths: [26, 69, 232, 52, 55, 65],
                body: [
                    [
                        { text: 'ที่', style: 'tableHeader', alignment: 'right' },
                        { text: 'รหัสสินค้า', style: 'tableHeader', alignment: 'center' },
                        { text: 'รายการ', style: 'tableHeader', alignment: 'center' },
                        { text: 'จำนวน', style: 'tableHeader', alignment: 'center' },
                        { text: 'ราคา/หน่วย', style: 'tableHeader', alignment: 'center' },
                        { text: 'รวม', style: 'tableHeader', alignment: 'center' }
                    ],
                    ...(receipt.items || []).map((item, index) => [
                        { text: (index + 1).toString(), alignment: 'right' }, // ✅ ลำดับที่
                        { text: item.barcode || '-', alignment: 'center' },
                        { text: buildReceiptItemDescription(item), alignment: 'left' },
                        { text: buildReceiptItemQuantity(item), alignment: 'center' },
                        { text: item.is_free ? 'แถมฟรี' : `${(formatCurrency(item.unit_price) || 0).toLocaleString()} `, alignment: 'right' },
                        { text: item.is_free ? '0.00' : `${(formatCurrency(item.total_price) || 0).toLocaleString()} `, alignment: 'right' }
                    ])
                ]
            },
            // layout: {
            //     fillColor: function (rowIndex, node, columnIndex) {
            //         return (rowIndex === 0) ? '#eeeeee' : null;
            //     }
            // },
            layout: 'noBorders',
            margin: [0, 0, 0, 20]
        },
        // วิธีการชำระเงิน
        // ...(receipt.payment_method ? [{
        //     text: 'วิธีการชำระเงิน',
        //     style: 'subheader',
        //     margin: [0, 5, 0, 0]
        // }, {
        //     text: receipt.payment_method,
        //     margin: [0, 0, 0, 5]
        // }] : []),

        // หมายเหตุ
        ...(receipt.notes ? [{
            text: 'หมายเหตุ',
            style: 'subheader',
            margin: [10, 5, 0, 0]
        }, {
            text: receipt.notes,
            margin: [10, 0, 0, 5]
        }] : []),


    ], //ส่วนท้ายกระดาษ (แสดงหน้าสุดท้ายของเอกสาร)
    footer: function (currentPage, pageCount) {
        if (currentPage !== pageCount) return {}; // แสดงเฉพาะหน้าสุดท้าย

        return {
            margin: [40, -200, 40, 0], // <— ดึงขึ้น (negative top margin สำคัญ)
            lineHeight: 0.9,   // <— ค่านี้ยิ่งน้อย บรรทัดจะยิ่งชิดกัน
            stack: [
                // ยอดรวม
                {
                    columns: [
                        {
                            lineHeight: 0.9,   // <— ค่านี้ยิ่งน้อย บรรทัดจะยิ่งชิดกัน
                            width: '70%',
                            style: 'bathText',
                            stack: [
                                // เงื่อนไขการชำระเงิน (สำหรับใบแจ้งหนี้)
                                {
                                    text: remainingAmount > 0 ? 'เงื่อนไขการชำระเงิน' : '',
                                    style: 'subheader',
                                    margin: [0, 10, 0, 5]
                                },
                                {
                                    text: remainingAmount > 0 ? `- กรุณาชำระเงินตามกำหนดเวลาที่ระบุไว้ หากมีข้อสงสัยกรุณาติดต่อกลับ` : '',
                                    margin: [15, 0, 0, 21]

                                },
                                //ตัวหนังสือใบเสร็จ
                                {
                                    text: `(${numberToThaiText(pdfTotalAmount)})`,
                                    margin: remainingAmount > 0 ? [25, 5, 0, 0] : [25, 40, 0, 0]
                                }
                            ],
                            margin: [5, 0, 40, 0], // Left, Top, Right, Bottom <— ดึงขึ้น (negative top margin สำคัญ)
                        },
                        // สรุปยอดเงิน
                        {
                            width: '53%',
                            lineHeight: 0.7, //ความสูงของแถว
                            table: {
                                body: [
                                    // ✅ ยอดรวมก่อนภาษี (ยังไม่หักส่วนลด)
                                    [
                                        { text: 'ยอดรวม:', alignment: 'right' },
                                        { text: `${formatCurrency(summary.grossAmount)}`, alignment: 'right', margin: [0, 0, 0, 0] },
                                    ],
                                    // ✅ ส่วนลด
                                    [
                                        { text: 'ส่วนลด:', alignment: 'right' },
                                        { text: `${formatCurrency(summary.discountAmount)}`, alignment: 'right', margin: [0, 0, 0, 0] },
                                    ],
                                    // ✅ ✅ เพิ่มมา: ราคาไม่รวมภาษี (subtotal - discount)
                                    [
                                        { text: amountAfterDiscountLabel, alignment: 'right', bold: true },
                                        {
                                            text: `${formatCurrency(summary.netAmount)}`,
                                            alignment: 'right',
                                            bold: true,
                                            margin: [0, 0, 0, 0]
                                        }
                                    ],
                                    // ✅ ภาษี
                                    ...(showTaxBreakdown ? [[
                                        { text: taxLabel, alignment: 'right' },
                                        { text: `${formatCurrency(summary.taxAmount)}`, alignment: 'right', margin: [0, 0, 0, 0] }
                                    ]] : []),
                                    // ✅ ยอดรวมทั้งสิ้น
                                    [
                                        { text: 'ยอดรวมทั้งสิ้น:', bold: true, alignment: 'right' },
                                        { text: `${formatCurrency(pdfTotalAmount)}`, bold: true, alignment: 'right', margin: [0, 0, 0, 0] }
                                    ],
                                    ...(remainingAmount > 0 ? [
                                        [
                                            { text: 'ชำระแล้ว:', alignment: 'right', color: '#52c41a' },
                                            { text: `${formatCurrency(paidAmount).toLocaleString()} บาท`, alignment: 'right', color: '#52c41a' }
                                        ],
                                        [
                                            { text: 'คงค้างชำระ:', bold: true, alignment: 'right', color: remainingAmount > 0 ? '#cf1322' : '#52c41a' },
                                            { text: `${formatCurrency(remainingAmount).toLocaleString()} บาท`, bold: true, alignment: 'right', color: remainingAmount > 0 ? '#cf1322' : '#52c41a' }
                                        ]
                                    ] : [])
                                ]
                            },
                            layout: 'noBorders'
                        }
                    ],
                    margin: [0, -5, 0, 20]
                },

                // ลายเซ็น
                {
                    columns: [
                        {
                            width: '50%',
                            stack: [
                                {
                                    text: `${receipt.status === 'paid' ? 'ผู้รับเงิน' : 'ผู้ออกเอกสาร'}`,
                                    alignment: 'center',
                                    margin: [0, 2, 0, 0],
                                },
                                ...(fixedSignatureLogo ? [ // ✅ ถ้ามีรูปจริง ค่อยใส่ image
                                    {
                                        image: fixedSignatureLogo,
                                        width: signaturePosition.width,
                                        alignment: 'center',
                                        relativePosition: { x: signaturePosition.x, y: signaturePosition.y },
                                        margin: [0, 0, 0, -Math.round(signaturePosition.width * 0.35)],
                                    }
                                ] : []),
                                {
                                    text: '_________________________________',
                                    alignment: 'center',
                                    margin: [0, 25, 0, 0],
                                },
                                {
                                    text: collectorName,
                                    alignment: 'center',
                                    margin: [0, 2, 0, 0],
                                },
                            ],
                        },
                        {
                            width: '50%',
                            stack: fixedStampLogo ? [
                                { image: fixedStampLogo, width: 78, alignment: 'center', margin: [0, 4, 0, 0] }
                            ] : [{ text: '' }]
                        },
                        //   {
                        //     width: '50%',
                        //     stack: [
                        //         {
                        //             text: `${receipt.status === 'paid' ? 'ผู้รับเงิน' : 'ผู้ออกเอกสาร'}`,
                        //             alignment: 'center',
                        //             margin: [0, 2, 0, 0],
                        //         },
                        //         ...(fixedSignatureLogo ? [ // ✅ ถ้ามีรูปจริง ค่อยใส่ image
                        //             {
                        //                 image: fixedSignatureLogo,
                        //                 width: 100,
                        //                 alignment: 'center',
                        //                 absolutePosition: remainingAmount > 0
                        //                     ? { x: -200, y: -65 }
                        //                     : { x: -200, y: -100 },
                        //             }
                        //         ] : []),
                        //         {
                        //             text: '_________________________________',
                        //             alignment: 'center',
                        //             margin: [0, 25, 0, 0],
                        //         },
                        //         {
                        //             text: `${company.signature_name}`,
                        //             alignment: 'center',
                        //             margin: [0, 2, 0, 0],
                        //         },
                        //     ],
                        // },
                        // {
                        //     width: '50%',
                        //     stack: [
                        //         {
                        //             text: [
                        //                 `ในนาม ${receipt.company_name}`,
                        //                 '\n\n',
                        //                 '_________________________________',
                        //                 '\n',
                        //                 'ผู้จ่ายเงิน',
                        //             ],
                        //             alignment: 'center'
                        //         }
                        //     ]
                        // },

                    ]
                },

            ]
        };
    }
    };
};


